/**
 * WindFieldPass.ts — compute pass producing a 2D local wind-shelter texture.
 *
 * Output: r32float (1 = exposed, 0 = fully sheltered). Sampled by water
 * shader to attenuate cascade amplitudes in lee zones at short range,
 * complementing the longer-range FetchPass.
 *
 * Re-runs only when wind direction or water level changes.
 */

import wgsl from "../shaders/windfield.wgsl?raw";
import type { TerrainRenderPass } from "./TerrainRenderPass";
import { createUniformBuffer } from "../gpu/Buffers";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";

const STRUCT_SIZE = 48; // 12 × f32

export interface WindFieldPassOptions {
  resolution?: number;
  /** Maximum march distance (m). Default 240. */
  maxRange?: number;
  /** Number of march steps. Default 32. */
  steps?: number;
  /** Exponential falloff length (m). Default 80. */
  decayL?: number;
  /** Reference blocker height for full shelter (m). Default 8. */
  blockerH?: number;
  /** 1 - speedFactor at full shelter. Default 0.7. */
  shelterMax?: number;
}

export class WindFieldPass implements RenderPass {
  readonly name = "WindFieldPass";

  readonly resolution: number;
  readonly maxRange: number;
  readonly steps: number;
  readonly decayL: number;
  readonly blockerH: number;
  readonly shelterMax: number;
  readonly extent: number;

  private device: GPUDevice;
  private terrainPass: TerrainRenderPass;
  private pipeline: GPUComputePipeline;
  private bindGroup: GPUBindGroup;
  private uniform: GPUBuffer;
  private texture: GPUTexture;
  readonly view: GPUTextureView;

  private lastWindDirDeg = NaN;
  private lastWaterLevel = NaN;

  constructor(
    device: GPUDevice,
    terrainPass: TerrainRenderPass,
    opts: WindFieldPassOptions = {},
  ) {
    this.device = device;
    this.terrainPass = terrainPass;
    this.resolution = opts.resolution ?? 256;
    this.maxRange = opts.maxRange ?? 240;
    this.steps = opts.steps ?? 32;
    this.decayL = opts.decayL ?? 80;
    this.blockerH = opts.blockerH ?? 8;
    this.shelterMax = opts.shelterMax ?? 0.7;
    this.extent = terrainPass.heightmap.extent;

    this.texture = device.createTexture({
      label: "WindFieldPass.tex",
      size: { width: this.resolution, height: this.resolution },
      format: "r32float",
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.view = this.texture.createView({ label: "WindFieldPass.view" });

    this.uniform = createUniformBuffer(
      device,
      STRUCT_SIZE,
      "WindFieldPass.params",
    );

    const module = device.createShaderModule({
      label: "windfield.wgsl",
      code: wgsl,
    });
    const bgl = device.createBindGroupLayout({
      label: "WindFieldPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: { access: "write-only", format: "r32float" },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "WindFieldPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      compute: { module, entryPoint: "cs_main" },
    });
    this.bindGroup = device.createBindGroup({
      label: "WindFieldPass.bg",
      layout: bgl,
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: terrainPass.heightmapView() },
        { binding: 2, resource: this.view },
      ],
    });
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const { state } = frame;
    const dirty =
      state.windDirectionDeg !== this.lastWindDirDeg ||
      this.terrainPass.waterLevel !== this.lastWaterLevel;
    if (!dirty) return;
    this.lastWindDirDeg = state.windDirectionDeg;
    this.lastWaterLevel = this.terrainPass.waterLevel;

    const a = (state.windDirectionDeg * Math.PI) / 180;
    const wx = -Math.sin(a);
    const wz = Math.cos(a);
    const data = new Float32Array([
      this.extent,
      this.resolution,
      wx,
      wz,
      this.terrainPass.waterLevel,
      this.maxRange,
      this.steps,
      this.decayL,
      this.blockerH,
      this.shelterMax,
      0,
      0,
    ]);
    this.device.queue.writeBuffer(this.uniform, 0, data);

    const pass = encoder.beginComputePass({ label: "WindFieldPass" });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    const wg = Math.ceil(this.resolution / 8);
    pass.dispatchWorkgroups(wg, wg, 1);
    pass.end();
  }

  destroy(): void {
    this.texture.destroy();
  }
}
